import { TEMPLATE_POOL_PAGE_SIZE } from '@/pages/EmailCenter/constants';
import {
  createPartnerTranslationRequest,
  fetchEmailComponentLabelsRequest,
  fetchEmailSettingsRequest,
  fetchEmailTemplatesRequest,
  fetchEmailTemplateTypesRequest,
  fetchEmailVariablesRequest,
  fetchJourneyEmailConfigRequest,
  fetchJourneyEmailTemplatesRequest,
  fetchMailersRequest,
  fetchMarketingAutomationsRequest,
  fetchPartnerLanguagesRequest,
  fetchPartnerTranslationsRequest,
  fetchTemplateFragmentsRequest,
  updateEmailSettingsRequest,
  updatePartnerTranslationRequest,
} from '@/services/api/emailCenter';
import { fetchJourneyConfigRequest } from '@/services/api/journey';
import {
  EmailDriver,
  EmailSettingsModel,
  EmailTemplateModel,
  EmailTemplateTypeModel,
  EmailVariablesModel,
  EmailVariablesResponse,
  JourneyEmailConfigModel,
  JourneyEmailConfigResponse,
  JourneyEmailTemplateLinkModel,
  JourneyThemeModel,
  LaravelPaginator,
  MailerModel,
  MarketingAutomationModel,
  PartnerTranslationModel,
  TemplateFragmentModel,
} from '@/typings/api/emailCenter';
import { processAPIResponse } from '@/utils/request';

export type EmailCenterDrawerName =
  | 'template'
  | 'preview'
  | 'emailConfig'
  | 'link'
  | 'automation'
  | 'mailer'
  | 'translation';

export interface DrawerState {
  visible: boolean;
  mode: 'create' | 'edit';
  record: unknown;
  /**
   * Which view the drawer's editor opens on, when the caller cares — a
   * deep link (`?view=`) can land straight on Preview. Null lets the editor
   * pick its own default.
   */
  view: string | null;
}

const closedDrawer = (): DrawerState => ({
  visible: false,
  mode: 'create',
  record: null,
  view: null,
});

const initialState = {
  // Full template pool per type (one max-size page, like the pickers) — the
  // Templates tab computes trigger coverage client-side, so pagination would
  // hide templates from the mapping. Totals expose truncation past the cap.
  templates: {
    transactional: [] as EmailTemplateModel[],
    marketing: [] as EmailTemplateModel[],
    transactionalTotal: 0,
    marketingTotal: 0,
    loaded: false,
  },
  templateTypes: [] as EmailTemplateTypeModel[],
  variables: null as EmailVariablesModel | null,
  variableDescriptions: {} as Record<string, string>,
  /**
   * Which namespaces our own render resolves, as `/email-variables` declares
   * them. Everything else the catalogue offers is the provider's.
   *
   * **Undefined is not the empty list.** Undefined means the server has not said
   * — an old build, or the fetch has not landed — and the editor then defers
   * *nothing* to send time, which is the behaviour from before send-time
   * conditionals existed. An empty list would instead mean "every namespace is
   * the provider's", and would wrap our own conditions in `{% verbatim %}` and
   * ship them to recipients as text.
   */
  renderTimeNamespaces: undefined as string[] | undefined,
  selectedJourneyHashId: null as string | null,
  // The selected journey's own email_config row. `exists` is the endpoint's
  // meta.exists — an existing but value-less row means emails are switched off
  // for the journey, a missing one that it inherits the partner default's
  // sender; null when the endpoint did not say. See `journeyEmailStatus`.
  journeyEmailConfig: {
    journeyHashId: null as string | null,
    config: {} as JourneyEmailConfigModel,
    exists: null as boolean | null,
    // The journey's resolved email provider (`meta.email_driver`); null when
    // the endpoint did not say. Read by `useJourneyEmailDriver`.
    driver: null as EmailDriver | null,
    loaded: false,
  },
  // Theme of the journey the theme tab / preview substitution works with.
  // loaded marks first resolution for that hash (even on error) so consumers
  // don't refetch in a loop against a broken endpoint.
  journeyTheme: {
    journeyHashId: null as string | null,
    theme: null as JourneyThemeModel | null,
    loaded: false,
  },
  journeyTemplates: [] as JourneyEmailTemplateLinkModel[],
  automations: [] as MarketingAutomationModel[],
  mailers: [] as MailerModel[],
  // Verified live: /mailers can 403 for partner admins — the tab renders a
  // "no access" state instead of error-toasting on every visit.
  mailersAccessDenied: false,
  translations: [] as PartnerTranslationModel[],
  // Twig layouts + partials for client-side preview rendering. Partner-wide
  // reference data like translations; fetched once per Email Center session.
  fragments: [] as TemplateFragmentModel[],
  fragmentsLoaded: false,
  /** Fully-qualified label key → resolved text, for the locale below. */
  componentLabels: {} as Record<string, string>,
  /** The locale `componentLabels` was resolved for; null until first fetch. */
  componentLabelsLocale: null as string | null,
  // Partner-wide email rendering settings (default layout etc.).
  emailSettings: { default_layout_key: null } as EmailSettingsModel,
  emailSettingsLoaded: false,
  /**
   * The languages this partner translates into. **Empty means every language** —
   * see `selectTranslationLanguages`, which is where that is resolved so no
   * consumer has to remember the rule.
   */
  partnerLanguages: [] as string[],
  partnerLanguagesLoaded: false,
  drawers: {
    template: closedDrawer(),
    preview: closedDrawer(),
    emailConfig: closedDrawer(),
    link: closedDrawer(),
    automation: closedDrawer(),
    mailer: closedDrawer(),
    translation: closedDrawer(),
  } as Record<EmailCenterDrawerName, DrawerState>,
};

export type EmailCenterState = typeof initialState;

/** Rows + totals from the Laravel paginator, tolerating both paginate() and API-resource shapes. */
export function readPaginator<T>(
  paginator: LaravelPaginator<T> | T[] | null | undefined,
): {
  list: T[];
  total: number;
  page: number;
} {
  if (Array.isArray(paginator)) {
    return { list: paginator, total: paginator.length, page: 1 };
  }
  const list = paginator?.data || [];
  const total = paginator?.meta?.total ?? paginator?.total ?? list.length;
  const page = paginator?.meta?.current_page ?? paginator?.current_page ?? 1;
  return { list, total, page };
}

export default {
  namespace: 'emailCenter',

  state: initialState,

  effects: {
    *fetchTemplates(_, { all, call, put }) {
      const [transactionalResult, marketingResult] = yield all([
        call(fetchEmailTemplatesRequest, {
          query: { type: 'transactional', page_size: TEMPLATE_POOL_PAGE_SIZE },
        }),
        call(fetchEmailTemplatesRequest, {
          query: { type: 'marketing', page_size: TEMPLATE_POOL_PAGE_SIZE },
        }),
      ]);

      yield processAPIResponse(
        transactionalResult,
        {
          *onSuccess(paginator) {
            const { list, total } = readPaginator(paginator);
            yield put({
              type: 'saveTemplates',
              payload: { transactional: list, transactionalTotal: total },
            });
          },
        },
        'data',
      );

      yield processAPIResponse(
        marketingResult,
        {
          *onSuccess(paginator) {
            const { list, total } = readPaginator(paginator);
            yield put({
              type: 'saveTemplates',
              payload: { marketing: list, marketingTotal: total },
            });
          },
        },
        'data',
      );

      // Marks first resolution even on error — empty states must not claim
      // "no templates" while a fetch has never completed.
      yield put({ type: 'saveTemplates', payload: { loaded: true } });
    },

    *fetchTemplateTypes(_, { call, put }) {
      const { response, data } = yield call(fetchEmailTemplateTypesRequest);
      yield processAPIResponse(
        { response, data },
        {
          *onSuccess(types) {
            yield put({ type: 'saveTemplateTypes', payload: types || [] });
          },
        },
        'data',
      );
    },

    *fetchVariables(_, { call, put }) {
      const { response, data } = yield call(fetchEmailVariablesRequest);
      yield processAPIResponse(
        { response, data },
        {
          *onSuccess(body: EmailVariablesResponse) {
            yield put({
              type: 'saveVariables',
              payload: {
                variables: body?.variables || null,
                variableDescriptions: body?.descriptions || {},
                // `undefined`, not `[]`, when the server did not say — the two
                // mean opposite things downstream. See `renderTimeNamespaces`
                // in the state above.
                renderTimeNamespaces: Array.isArray(
                  body?.render_time_namespaces,
                )
                  ? body.render_time_namespaces
                  : undefined,
              },
            });
          },
        },
        'data',
      );
    },

    *fetchJourneyEmailConfig({ payload }, { call, put }) {
      const { journeyHashId } = payload;
      if (!journeyHashId) return;
      const { response, data } = yield call(fetchJourneyEmailConfigRequest, {
        journeyHashId,
      });
      let config: JourneyEmailConfigModel = {};
      let exists: boolean | null = null;
      let driver: EmailDriver | null = null;
      yield processAPIResponse(
        { response, data },
        {
          *onSuccess(body: JourneyEmailConfigResponse) {
            config = body?.data || {};
            exists =
              typeof body?.meta?.exists === 'boolean' ? body.meta.exists : null;
            driver = body?.meta?.email_driver || null;
          },
        },
      );
      // Stored even when the request failed (loaded marks first resolution, like
      // the theme) so consumers don't refetch in a loop against a broken endpoint.
      yield put({
        type: 'saveJourneyEmailConfig',
        payload: { journeyHashId, config, exists, driver, loaded: true },
      });
    },

    *fetchJourneyTheme({ payload }, { call, put }) {
      const { journeyHashId } = payload;
      if (!journeyHashId) return;
      const { response, data } = yield call(fetchJourneyConfigRequest, {
        params: { journeyId: journeyHashId },
      });
      let theme: JourneyThemeModel | null = null;
      yield processAPIResponse(
        { response, data },
        {
          *onSuccess(view) {
            theme = (view as any)?.configuration?.theme ?? {};
          },
        },
        'data',
      );
      yield put({
        type: 'saveJourneyTheme',
        payload: { journeyHashId, theme, loaded: true },
      });
    },

    *fetchJourneyTemplates({ payload }, { call, put }) {
      const { journeyHashId } = payload;
      if (!journeyHashId) return;
      const { response, data } = yield call(fetchJourneyEmailTemplatesRequest, {
        journeyHashId,
      });
      yield processAPIResponse(
        { response, data },
        {
          *onSuccess(links) {
            yield put({ type: 'saveJourneyTemplates', payload: links || [] });
          },
        },
        'data',
      );
    },

    *fetchAutomations({ payload }, { call, put }) {
      const { journeyHashId } = payload;
      if (!journeyHashId) return;
      const { response, data } = yield call(fetchMarketingAutomationsRequest, {
        journeyHashId,
      });
      yield processAPIResponse(
        { response, data },
        {
          *onSuccess(automations) {
            yield put({ type: 'saveAutomations', payload: automations || [] });
          },
        },
        'data',
      );
    },

    *fetchMailers(_, { call, put }) {
      const { response, data } = yield call(fetchMailersRequest);
      const accessDenied = response?.status === 403;
      yield processAPIResponse(
        { response, data },
        {
          *onSuccess(mailers) {
            yield put({
              type: 'saveMailers',
              payload: { mailers: mailers || [], accessDenied: false },
            });
          },
          *onError() {
            yield put({
              type: 'saveMailers',
              payload: { mailers: [], accessDenied },
            });
          },
        },
        'data',
      );
    },

    *fetchFragments({ payload }, { call, put, select }) {
      // Reference data — skip refetching once resolved (unless forced after
      // a fragment write).
      const loaded = yield select(
        (state: any) => state.emailCenter.fragmentsLoaded,
      );
      if (loaded && !payload?.force) return;

      const { response, data } = yield call(fetchTemplateFragmentsRequest);
      yield processAPIResponse(
        { response, data },
        {
          *onSuccess(fragments) {
            yield put({
              type: 'saveFragments',
              payload: fragments || [],
            });
          },
        },
        'data',
      );
    },

    /**
     * Editor labels for the core components. Reference data, but keyed by
     * locale: a refetch is needed when the user switches the backoffice
     * language, so the guard compares the resolved locale rather than a
     * loaded flag.
     */
    *fetchComponentLabels({ payload }, { call, put, select }) {
      const locale = payload?.locale;
      const resolved = yield select(
        (state: any) => state.emailCenter.componentLabelsLocale,
      );
      if (resolved !== null && !payload?.force) {
        // A request without an explicit locale accepts whatever is loaded; with
        // one, only a matching locale counts as already resolved.
        if (!locale || resolved === locale.slice(0, 2)) return;
      }

      const { response, data } = yield call(fetchEmailComponentLabelsRequest, {
        params: { locale },
      });
      yield processAPIResponse(
        { response, data },
        {
          *onSuccess(result) {
            yield put({
              type: 'saveComponentLabels',
              payload: {
                labels: result?.labels || {},
                locale: result?.locale || null,
              },
            });
          },
        },
        'data',
      );
    },

    /**
     * Reference data, fetched once per session like the settings above.
     *
     * The reducer it writes through is `storePartnerLanguages`, deliberately not
     * `savePartnerLanguages`: dva dispatches a name to the reducer *and* the
     * effect of that name, and an effect/reducer pair sharing one name is what
     * made every read of the email settings re-enter their writer and PUT an
     * empty body. One name, one handler.
     */
    *fetchPartnerLanguages({ payload }, { call, put, select }) {
      const loaded = yield select(
        (state: any) => state.emailCenter.partnerLanguagesLoaded,
      );
      if (loaded && !payload?.force) return;

      const { response, data } = yield call(fetchPartnerLanguagesRequest);
      yield processAPIResponse(
        { response, data },
        {
          *onSuccess(result) {
            yield put({
              type: 'storePartnerLanguages',
              payload: result?.languages ?? [],
            });
          },
          *onError() {
            // Mark resolved anyway: consumers fall back to every language, which
            // is the same thing an unset preference means.
            yield put({ type: 'storePartnerLanguages', payload: [] });
          },
        },
        'data',
      );
    },

    *fetchEmailSettings({ payload }, { call, put, select }) {
      // Reference data — skip refetching once resolved (unless forced after
      // a settings write elsewhere).
      const loaded = yield select(
        (state: any) => state.emailCenter.emailSettingsLoaded,
      );
      if (loaded && !payload?.force) return;

      const { response, data } = yield call(fetchEmailSettingsRequest);
      yield processAPIResponse(
        { response, data },
        {
          *onSuccess(settings) {
            yield put({
              type: 'storeEmailSettings',
              payload: settings ?? { default_layout_key: null },
            });
          },
        },
        'data',
      );
    },

    /**
     * Persists the partner-wide email settings (currently the default
     * layout). The saved server state replaces the local copy on success.
     */
    *saveEmailSettings({ payload }, { call, put }) {
      const { settings, onSuccess, onError } = payload;
      const { response, data } = yield call(updateEmailSettingsRequest, {
        payload: settings,
      });
      yield processAPIResponse(
        { response, data },
        {
          showSuccessMessage: true,
          *onSuccess(saved) {
            yield put({
              type: 'storeEmailSettings',
              payload: saved ?? settings,
            });
            if (onSuccess) onSuccess();
          },
          *onError(error: unknown) {
            if (onError) onError(error);
          },
        },
        'data',
      );
    },

    *fetchTranslations(_, { call, put }) {
      const { response, data } = yield call(fetchPartnerTranslationsRequest);
      yield processAPIResponse(
        { response, data },
        {
          *onSuccess(translations) {
            yield put({
              type: 'saveTranslations',
              payload: translations || [],
            });
          },
        },
        'data',
      );
    },

    /**
     * Persists wording for one key, keyed by the `group.key` form the Twig `trans`
     * filter uses. Creates the partner translation when the partner has none for that
     * group/key, otherwise merges the given languages into the existing value map,
     * then refreshes the catalog so previews resolve the new value.
     *
     * **`values` carries every language in one request, and that is load-bearing.** The
     * merge below reads `existing.value` out of the catalog, and the refetch is
     * dispatched rather than awaited — so two overlapping saves would both merge into
     * the *same* stale map and the second would drop the first's language. Editing
     * thirteen languages therefore sends one PUT, not thirteen.
     */
    *saveTranslation({ payload }, { call, put, select }) {
      const { fullKey, language, value, values, onSuccess, onError } = payload;
      const [group, ...keyParts] = String(fullKey).split('.');
      const key = keyParts.join('.');
      // `language`/`value` is the single-language shorthand.
      const merged: Record<string, string> = values ?? { [language]: value };

      const translations: PartnerTranslationModel[] = yield select(
        (state: any) => state.emailCenter.translations,
      );
      const existing = translations.find(
        (translation) => translation.group === group && translation.key === key,
      );

      const { response, data } = existing
        ? yield call(updatePartnerTranslationRequest, {
            params: { uuid: existing.id },
            payload: { value: { ...existing.value, ...merged } },
          })
        : yield call(createPartnerTranslationRequest, {
            payload: {
              translation_group: group,
              key,
              value: merged,
            },
          });

      yield processAPIResponse(
        { response, data },
        {
          showSuccessMessage: true,
          *onSuccess() {
            yield put({ type: 'fetchTranslations' });
            if (onSuccess) onSuccess();
          },
          *onError(error: unknown) {
            if (onError) onError(error);
          },
        },
        'data',
      );
    },
  },

  reducers: {
    saveTemplates(state: EmailCenterState, { payload }) {
      return { ...state, templates: { ...state.templates, ...payload } };
    },
    saveTemplateTypes(state: EmailCenterState, { payload }) {
      return { ...state, templateTypes: payload };
    },
    saveVariables(state: EmailCenterState, { payload }) {
      return {
        ...state,
        variables: payload.variables,
        variableDescriptions: payload.variableDescriptions,
        renderTimeNamespaces: payload.renderTimeNamespaces,
      };
    },
    setSelectedJourney(state: EmailCenterState, { payload }) {
      if (payload === state.selectedJourneyHashId) return state;
      // Journey-scoped data belongs to the previous journey — drop it so the
      // UI never shows another journey's config while the refetch is in flight.
      return {
        ...state,
        selectedJourneyHashId: payload,
        journeyEmailConfig: {
          journeyHashId: null,
          config: {},
          exists: null,
          driver: null,
          loaded: false,
        },
        journeyTheme: { journeyHashId: null, theme: null, loaded: false },
        journeyTemplates: [],
        automations: [],
      };
    },
    saveJourneyEmailConfig(state: EmailCenterState, { payload }) {
      return { ...state, journeyEmailConfig: payload };
    },
    saveJourneyTheme(state: EmailCenterState, { payload }) {
      return { ...state, journeyTheme: payload };
    },
    saveJourneyTemplates(state: EmailCenterState, { payload }) {
      return { ...state, journeyTemplates: payload };
    },
    saveAutomations(state: EmailCenterState, { payload }) {
      return { ...state, automations: payload };
    },
    saveMailers(state: EmailCenterState, { payload }) {
      return {
        ...state,
        mailers: payload.mailers,
        mailersAccessDenied: payload.accessDenied,
      };
    },
    saveTranslations(state: EmailCenterState, { payload }) {
      return { ...state, translations: payload };
    },
    saveComponentLabels(state: EmailCenterState, { payload }) {
      return {
        ...state,
        componentLabels: payload.labels,
        componentLabelsLocale: payload.locale,
      };
    },

    saveFragments(state: EmailCenterState, { payload }) {
      return { ...state, fragments: payload, fragmentsLoaded: true };
    },
    /**
     * Named `store…`, not `save…`, because there is an **effect** called
     * `saveEmailSettings` that PUTs. dva dispatches a name to the reducer *and*
     * the effect of that name, so while both existed, every read of the settings
     * re-entered the writer: its `payload` was the settings object rather than
     * `{ settings }`, so it PUT an empty body and the server answered 422.
     * Opening the template drawer fetches the settings, which is where it showed.
     */
    storePartnerLanguages(state: EmailCenterState, { payload }) {
      return {
        ...state,
        partnerLanguages: Array.isArray(payload) ? payload : [],
        partnerLanguagesLoaded: true,
      };
    },
    storeEmailSettings(state: EmailCenterState, { payload }) {
      return { ...state, emailSettings: payload, emailSettingsLoaded: true };
    },
    openDrawer(state: EmailCenterState, { payload }) {
      const { drawer, mode = 'create', record = null, view = null } = payload;
      return {
        ...state,
        drawers: {
          ...state.drawers,
          [drawer]: { visible: true, mode, record, view },
        },
      };
    },
    /**
     * Merge fields into the record the open drawer was given, without reopening it.
     *
     * Saving a draft used to `close()` and let the URL's `?template=` reopen the
     * drawer — which reopened it from the *pool*, before the refetch that would
     * have carried `has_draft` had landed. So the draft banner and its
     * publish/discard actions only appeared after a manual reload: the drawer was
     * showing a record that predated the save. Patching in place keeps the editor
     * mounted (a draft save means "I am still working") and its state honest.
     */
    patchDrawerRecord(state: EmailCenterState, { payload }) {
      const { drawer, record } = payload;
      const current = state.drawers[drawer];
      if (!current?.visible || !current.record) return state;

      return {
        ...state,
        drawers: {
          ...state.drawers,
          [drawer]: { ...current, record: { ...current.record, ...record } },
        },
      };
    },
    closeDrawer(state: EmailCenterState, { payload }) {
      const { drawer } = payload;
      return {
        ...state,
        drawers: {
          ...state.drawers,
          [drawer]: closedDrawer(),
        },
      };
    },
    reset() {
      return initialState;
    },
  },
};
